/**
 * Home Screen Editor
 * 
 * Allows caregivers to edit home screen items (icon, label)
 * with undo support and drag-to-reorder functionality.
 * Supports both flower (cross) and row layouts for editing.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { Pencil, Check, Undo2, RotateCcw, GripVertical, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { BoardItemEditForm } from "@/components/communication/BoardItemEditForm";
import { ICON_MAP } from "@/components/ui/LucideIconPicker";
import { getNavigationColorClasses } from "@/components/communication/NavigationColorPicker";

import { getUserSettings } from "@/lib/userSettings";
import { resolveInputBehavior, SCAN_MODES } from "@/lib/inputMethods";
import { useTranslation } from "@/hooks/useTranslation";
import {
  NavigationBoardData,
  NavigationItem,
  loadNavigationBoard,
  saveNavigationBoard,
  updateNavigationItem,
  reorderNavigationItems,
  swapNavigationSlots,
  resetNavigationBoard,
  getDefaultItems,
  getSlotKey,
} from "@/lib/navigationBoardTypes";
import { useUnsavedEditGuard } from "@/hooks/useUnsavedEditGuard";

const NAV_STORAGE_KEY = "navigationBoards_v21";

const MAX_UNDO_HISTORY = 20;

type LayoutMode = "row" | "flower";

/**
 * Determine initial layout mode using the same Control Settings logic as HomeGrid.
 * Row is used for auto/step scanning (including 3 gestures or fewer).
 */
function getInitialLayoutMode(): LayoutMode {
  const settings = getUserSettings();
  const controlMethod = settings.controlMethod;

  let inputCount = 1;
  if (controlMethod === 'external_switch' || controlMethod === 'switch_control') {
    inputCount = settings.externalSwitchCount || 1;
  } else if (controlMethod === 'eye_control' || controlMethod === 'eyecontrol') {
    inputCount = settings.eyeControlGestures?.length || 1;
  } else if (controlMethod === 'mouth_gestures') {
    inputCount = settings.mouthGestures?.length || 1;
  }

  const behavior = resolveInputBehavior(controlMethod, inputCount);
  if (behavior.scanning === SCAN_MODES.AUTO || behavior.scanning === SCAN_MODES.STEP) {
    return 'row';
  }

  return 'flower';
}

export default function HomeEditor() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useCaregiverTranslation();
  const { isRTL: isUserRTL } = useTranslation();
  
  const [board, setBoard] = useState<NavigationBoardData>(() => loadNavigationBoard("home"));
  const [editingItem, setEditingItem] = useState<NavigationItem | null>(null);
  const [undoHistory, setUndoHistory] = useState<NavigationBoardData[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  // Slot key currently being dragged in fixed-slot (flower/cross) mode.
  // Independent of draggedIndex which is used for row-based reorder.
  const [draggedSlotKey, setDraggedSlotKey] = useState<string | null>(null);
  // Layout mode is locked to Control Settings — not user-editable from this page.
  const [layoutMode] = useState<LayoutMode>(getInitialLayoutMode);
  const isUndoing = useRef(false);

  const { markSaved } = useUnsavedEditGuard({
    storageKeys: [NAV_STORAGE_KEY],
    onAfterRevert: () => setBoard(loadNavigationBoard("home")),
  });

  // Safety: HTML5 drag events can sometimes miss onDragEnd (esp. after quick clicks).
  // Clear drag visual state on any drag/mouse/touch end so no other tile stays dim.
  useEffect(() => {
    const clearDragState = () => { setDraggedIndex(null); setDraggedSlotKey(null); };
    window.addEventListener("dragend", clearDragState);
    window.addEventListener("drop", clearDragState);
    window.addEventListener("mouseup", clearDragState);
    window.addEventListener("touchend", clearDragState);
    return () => {
      window.removeEventListener("dragend", clearDragState);
      window.removeEventListener("drop", clearDragState);
      window.removeEventListener("mouseup", clearDragState);
      window.removeEventListener("touchend", clearDragState);
    };
  }, []);

  // Save to localStorage when board changes
  useEffect(() => {
    if (!isUndoing.current) {
      saveNavigationBoard(board);
    }
  }, [board]);

  // Save to undo history before making changes
  const saveToHistory = useCallback(() => {
    setUndoHistory(prev => {
      const newHistory = [...prev, board];
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, [board]);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) return;
    
    isUndoing.current = true;
    const previousState = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setBoard(previousState);
    saveNavigationBoard(previousState);
    setDraggedIndex(null);
    
    setTimeout(() => {
      isUndoing.current = false;
    }, 0);
  }, [undoHistory]);

  // Get label in user's language for a navigation item
  const getUserLangLabel = (item: NavigationItem): string => {
    const userLang = getUserSettings().language || "he";
    if (userLang === "ar") return item.labelAr || item.labelEn;
    if (userLang === "ru") return item.labelRu || item.labelEn;
    if (userLang === "he") return item.labelHe;
    return item.labelEn;
  };

  // Get the update object targeting the user's language field
  const getUserLangUpdate = (name: string): Partial<NavigationItem> => {
    const userLang = getUserSettings().language || "he";
    if (userLang === "ar") return { labelAr: name };
    if (userLang === "ru") return { labelRu: name };
    if (userLang === "he") return { labelHe: name };
    return { labelEn: name };
  };

  // Handle save from edit form
  const handleSaveEdit = (data: { name: string; emoji: string; color: string; showEmoji: boolean; iconName?: string }) => {
    if (!editingItem) return;
    saveToHistory();
    
    const updatedBoard = updateNavigationItem(board, editingItem.id, {
      ...getUserLangUpdate(data.name),
      iconName: data.iconName || editingItem.iconName,
      color: data.color,
    });
    saveNavigationBoard(updatedBoard);
    markSaved();
    window.dispatchEvent(new Event('navigationBoardUpdated'));
    setBoard(updatedBoard);
    setEditingItem(null);
  };

  // Handle reset to defaults
  const handleResetToDefault = () => {
    saveToHistory();
    const defaultBoard = resetNavigationBoard("home");
    setBoard(defaultBoard);
    setDraggedIndex(null);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    saveToHistory();
    const newBoard = reorderNavigationItems(board, draggedIndex, index);
    setBoard(newBoard);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDraggedSlotKey(null);
  };

  // Slot-based drag (flower/cross layout). Allows swapping any item with any
  // other slot — including currently hidden slots — without changing item
  // identity. Used in addition to the row-based reorder above.
  const handleSlotDragStart = (slotKey: string) => {
    setDraggedSlotKey(slotKey);
  };

  const handleSlotDragOver = (e: React.DragEvent, slotKey: string) => {
    e.preventDefault();
    if (!draggedSlotKey || draggedSlotKey === slotKey) return;
    saveToHistory();
    const newBoard = swapNavigationSlots(board, draggedSlotKey, slotKey);
    setBoard(newBoard);
    setDraggedSlotKey(slotKey);
  };


  // If editing, show the edit form
  if (editingItem) {
    return (
      <BoardItemEditForm
        type="navigation"
        initialName={getUserLangLabel(editingItem)}
        initialEmoji=""
        initialColor={editingItem.color as any}
        initialIconName={editingItem.iconName}
        iconMode="lucide"
        iconOnly
        onSave={handleSaveEdit}
        onCancel={() => setEditingItem(null)}
      />
    );
  }

  // Get sorted items
  const sortedItems = [...board.items].sort((a, b) => a.order - b.order);

  // Handle visibility toggle
  const handleVisibilityToggle = (itemId: string, visible: boolean) => {
    setDraggedIndex(null);
    saveToHistory();
    const updatedBoard = updateNavigationItem(board, itemId, { visible });
    setBoard(updatedBoard);
  };

  // Render a single editable item
  // Light/dark mode custom color classes per item key (matches HomeGrid)
  // Only apply hardcoded CSS class when item still has its default color
  const defaultItems = getDefaultItems("home");
  const HOME_CARD_CLASSES: Record<string, string> = {
    communication: 'home-card-communication',
    keyboard: 'home-card-keyboard',
    entertainment: 'home-card-entertainment',
    rest: 'home-card-rest',
    help: 'home-card-help',
  };
  const getHomeCardClass = (item: NavigationItem) => {
    const defaultColor = defaultItems.find(d => d.key === item.key)?.color;
    if (defaultColor && item.color === defaultColor) {
      return HOME_CARD_CLASSES[item.key] || '';
    }
    return '';
  };

  const renderItem = (item: NavigationItem, index: number, className?: string, useSlotDrag?: boolean) => {
    const IconComponent = ICON_MAP[item.iconName] || ICON_MAP.Home;
    const userLang = getUserSettings().language || "he";
    const label = userLang === "ar" ? (item.labelAr || item.labelEn) 
                : userLang === "ru" ? (item.labelRu || item.labelEn)
                : userLang === "he" ? item.labelHe 
                : item.labelEn;
    const isVisible = item.visible === false ? false : true;
    const colorClasses = getNavigationColorClasses(item.color);
    const isCustomColor = !getHomeCardClass(item);
    const slotKey = getSlotKey(item);
    // Inline style for custom colors — needed to override !important CSS and element override inline styles
    const customColorStyle: React.CSSProperties = {};
    if (!isVisible) { customColorStyle.opacity = 0.4; customColorStyle.filter = 'grayscale(1)'; }
    if (isCustomColor && colorClasses.hex) { customColorStyle.borderColor = colorClasses.hex; }
    
    const isDragging = useSlotDrag ? draggedSlotKey === slotKey : draggedIndex === index;

    return (
      <motion.div
        key={`${item.id}-${item.visible}-${item.color}`}
        draggable
        onDragStart={() => useSlotDrag ? handleSlotDragStart(slotKey) : handleDragStart(index)}
        onDragOver={(e) => useSlotDrag ? handleSlotDragOver(e, slotKey) : handleDragOver(e, index)}
        onDragEnd={handleDragEnd}
        className={cn(
          "relative aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-2",
          "transition-all duration-200 cursor-move",
          "bg-card shadow-md",
          colorClasses.border,
          getHomeCardClass(item),
          isDragging && "opacity-50 scale-95",
          className
        )}
        style={Object.keys(customColorStyle).length ? customColorStyle : undefined}
        data-design-id={isCustomColor ? undefined : `home-card-${item.id}`}
        whileHover={{ scale: 1.02 }}
      >
        {/* Drag handle — fixed neutral color, independent of card theme */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 pointer-events-none">
          <GripVertical className="w-5 h-5" style={{ color: '#94a3b8', stroke: '#94a3b8' }} />
        </div>
        
        {/* Edit button — fixed teal background, white icon (independent of card theme) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDraggedIndex(null);
            setEditingItem(item);
          }}
          className={cn(
            "absolute top-3 p-2 rounded-full z-10 transition-opacity hover:opacity-80",
            isRTL ? "right-3" : "left-3"
          )}
          style={{ backgroundColor: 'hsl(184 65% 34%)' }}
        >
          <Pencil className="w-5 h-5" style={{ color: '#ffffff', stroke: '#ffffff' }} />
        </button>
        
        {/* Visibility toggle button — fixed orange background, white icon (independent of card theme) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDraggedIndex(null);
            handleVisibilityToggle(item.id, !isVisible);
          }}
          className={cn(
            "absolute top-3 p-2 rounded-full z-10 transition-opacity hover:opacity-80",
            isRTL ? "left-3" : "right-3"
          )}
          style={{ backgroundColor: isVisible ? 'hsl(20 95% 66%)' : '#9ca3af' }}
        >
          {isVisible ? (
            <Eye className="w-5 h-5" style={{ color: '#ffffff', stroke: '#ffffff' }} />
          ) : (
            <EyeOff className="w-5 h-5" style={{ color: '#ffffff', stroke: '#ffffff' }} />
          )}
        </button>
        
        <IconComponent 
          className={cn(colorClasses.iconColor, getHomeCardClass(item) ? "text-primary" : "")}
          strokeWidth={2}
          style={{ 
            width: 'clamp(2.5rem, 10vh, 5rem)', 
            height: 'clamp(2.5rem, 10vh, 5rem)',
            ...(isCustomColor && colorClasses.hex ? { color: colorClasses.hex } : {}),
          }}
        />
        <span 
          dir="auto" 
          className="font-semibold text-card-foreground text-center px-2 leading-tight"
          style={{ fontSize: 'clamp(1rem, 2.8vh, 1.5rem)' }}
        >
          {label}
        </span>
      </motion.div>
    );
  };

  // Render row layout (5 columns) - mirrors the live Home Screen exactly:
  // items are sorted by saved `order` ascending; in LTR index-0 sits on the
  // leftmost slot, and in RTL index-0 sits on the rightmost slot.
  const renderRowLayout = () => {
    const displayItems = isRTL ? [...sortedItems].reverse() : sortedItems;
    // Match HomeGrid row sizing: uniform square per slot.
    const itemSize = 'min(calc((100vw - 6rem) / 5 - 1rem), calc(100vh - 12rem))';
    return (
      <div className="flex flex-row items-center justify-center gap-4 w-full">
        {displayItems.map((item, index) => {
          // Find the original index in sortedItems so reorder/edit operations
          // act on the correct underlying item regardless of visual direction.
          const originalIndex = sortedItems.findIndex(i => i.id === item.id);
          return (
            <div
              key={item.id}
              style={{ width: itemSize, height: itemSize, flexShrink: 0 }}
            >
              {renderItem(item, originalIndex, "w-full h-full")}
            </div>
          );
        })}
      </div>
    );
  };

  // Render flower (cross) layout — positions are mapped by SLOT key (slotKey
  // falls back to key) so users can swap any item with any slot, including
  // currently hidden ones, without changing item identity.
  const renderFlowerLayout = () => {
    const bySlot = (slot: string) => sortedItems.find(i => getSlotKey(i) === slot);
    const indexBySlot = (slot: string) => sortedItems.findIndex(i => getSlotKey(i) === slot);

    const centerItem = bySlot('help');
    const topItem = bySlot('communication');
    const bottomItem = bySlot('entertainment');
    const restItem = bySlot('rest');
    const keyboardItem = bySlot('keyboard');

    if (!centerItem || !topItem || !keyboardItem || !restItem || !bottomItem) {
      return renderRowLayout();
    }

    // Mirror sides based on language (matches HomeGrid where dir handles mirroring;
    // here we use dir="ltr" on the grid so we mirror manually for RTL).
    const rightItem = isRTL ? restItem : keyboardItem;
    const leftItem = isRTL ? keyboardItem : restItem;
    const rightIndex = indexBySlot(isRTL ? 'rest' : 'keyboard');
    const leftIndex = indexBySlot(isRTL ? 'keyboard' : 'rest');

    return (
      <div 
        dir="ltr"
        className="grid grid-cols-3 grid-rows-3 gap-2"
        style={{ 
          aspectRatio: '1/1',
          height: 'min(100%, calc(100vw - 6rem))',
          maxHeight: 'calc(100vh - 7rem)',
          width: 'auto',
        }}
      >
        {/* Top row - Speak */}
        <div className="col-start-2 row-start-1">
          {renderItem(topItem, indexBySlot('communication'), undefined, true)}
        </div>
        
        {/* Middle row */}
        <div className="col-start-3 row-start-2">
          {renderItem(rightItem, rightIndex, undefined, true)}
        </div>
        <div className="col-start-2 row-start-2">
          {renderItem(centerItem, indexBySlot('help'), undefined, true)}
        </div>
        <div className="col-start-1 row-start-2">
          {renderItem(leftItem, leftIndex, undefined, true)}
        </div>
        
        {/* Bottom row - Media */}
        <div className="col-start-2 row-start-3">
          {renderItem(bottomItem, indexBySlot('entertainment'), undefined, true)}
        </div>
      </div>
    );
  };

  return (
    <MainLayout onBack={() => navigate(-1)}>
      <div className="flex-1 flex flex-col overflow-hidden bg-secondary dark:bg-background" dir={dir}>
        {/* Header with toggle integrated */}
        <div className="flex items-center justify-between px-4 py-1.5 bg-card/50 border-b border-border">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-foreground">
              {isRTL ? "עריכת מסך הבית" : "Edit home screen"}
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleUndo}
              disabled={undoHistory.length === 0}
            >
              <Undo2 className="w-4 h-4 mr-2" />
              {t("undo")}
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleResetToDefault}>
              <RotateCcw className="w-4 h-4 mr-2" />
              {t("resetToDefault")}
            </Button>
            
            <Button size="sm" disabled={undoHistory.length === 0} onClick={() => {
              markSaved();
              window.dispatchEvent(new Event('navigationBoardUpdated'));
              navigate('/board-editor');
            }}>
              <Check className="w-4 h-4 mr-2" />
              {t("doneEditing")}
            </Button>
          </div>
        </div>

        {/* Items grid - no scrolling, matches HomeGrid sizing */}
        <div className="flex-1 flex items-center justify-center p-2 min-h-0">
          {layoutMode === "row" ? renderRowLayout() : renderFlowerLayout()}
        </div>
      </div>
    </MainLayout>
  );
}
